<template>
  <div class="a-table" :class="{ 'a-table--loading': loading }">
    <table class="a-table__el">
      <thead>
        <tr>
          <th
            v-for="col in columns"
            :key="col.key"
            :class="[`a-th--${col.align || 'left'}`, col.thClass]"
            :style="col.width ? { width: typeof col.width === 'number' ? `${col.width}px` : col.width } : {}"
          >
            <slot :name="`head:${col.key}`" :col="col">{{ col.label }}</slot>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-if="rows && rows.length">
          <tr
            v-for="(row, idx) in rows"
            :key="rowKey ? row[rowKey] : idx"
            @click="$emit('row-click', row, idx)"
          >
            <td
              v-for="col in columns"
              :key="col.key"
              :class="[`a-td--${col.align || 'left'}`, col.tdClass, { 'a-td--mono': col.mono }]"
            >
              <slot :name="`cell:${col.key}`" :row="row" :value="row[col.key]" :index="idx">
                {{ row[col.key] }}
              </slot>
            </td>
          </tr>
        </template>
        <tr v-else>
          <td :colspan="columns.length" class="a-table__empty-cell">
            <slot name="empty">
              <AEmpty :title="emptyText" />
            </slot>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="loading" class="a-table__loading">
      <div class="p-spinner" />
    </div>
  </div>
</template>

<script>
import AEmpty from './AEmpty.vue';

export default {
  name: 'ATable',
  components: { AEmpty },
  emits: ['row-click'],
  props: {
    columns: { type: Array, required: true },
    rows:    { type: Array, default: () => [] },
    rowKey:  { type: String, default: '' },
    loading: { type: Boolean, default: false },
    emptyText: { type: String, default: '데이터가 없습니다' },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/pineapple';

.a-table {
  position: relative;
  width: 100%;
  background: $p-panel;
  border-radius: $p-radius-lg;
  overflow: hidden;
  border: 1px solid $p-line;
}

.a-table__el {
  width: 100%;
  border-collapse: collapse;
  font-family: $p-font-body;

  thead th {
    background: $p-panel-alt;
    color: $p-ink-mid;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    text-align: left;
    padding: 12px 14px;
    border-bottom: 1px solid $p-line;
    white-space: nowrap;
  }

  tbody td {
    padding: 12px 14px;
    border-bottom: 1px solid $p-line-soft;
    color: $p-ink;
    font-size: 13px;
    vertical-align: middle;
  }

  tbody tr:last-child td { border-bottom: 0; }
  tbody tr:hover { background: $p-panel-alt; }
}

// thead th 의 text-align: left 규칙보다 우선하도록 같은 깊이로 지정한다
// (그렇지 않으면 헤더가 col.align 을 무시하고 항상 왼쪽 정렬된다)
.a-table__el thead th.a-th--center,
.a-table__el tbody td.a-td--center { text-align: center; }

.a-table__el thead th.a-th--right,
.a-table__el tbody td.a-td--right { text-align: right; }

.a-td--mono { font-family: $p-font-mono; }

.a-table__empty-cell {
  padding: 0 !important;
  border-bottom: 0 !important;
  &:hover { background: transparent !important; }
}

.a-table__loading {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  display: grid;
  place-items: center;
}

.p-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid $p-line;
  border-top-color: $p-point;
  border-radius: 50%;
  animation: a-spin 0.7s linear infinite;
}

@keyframes a-spin {
  to { transform: rotate(360deg); }
}
</style>
